import React from 'react';

const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

// Poll the shared conversation so everyone's screens stay in step.
export function useGroupSync({ conversationId, active, busy, onSync }) {
  const busyRef = React.useRef(busy);
  busyRef.current = busy;
  React.useEffect(() => {
    if (!conversationId || !active) return undefined;
    let stop = false;
    const tick = async () => {
      if (busyRef.current) return;
      try {
        const r = await fetch(`${BASE}/mira/history/${conversationId}`, { credentials: 'include' });
        if (!r.ok) return;
        const j = await r.json();
        if (!stop) onSync(j);
      } catch { /* transient */ }
    };
    const t = setInterval(tick, 4000);
    return () => { stop = true; clearInterval(t); };
  }, [conversationId, active, onSync]);
}

export function InviteModal({ conversationId, onClose, onCreated }) {
  const [inv, setInv] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    fetch(`${BASE}/mira/invite`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_id: conversationId }),
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (ok) { setInv(j); onCreated && onCreated(j); }
        else setErr((j.detail && j.detail.message) || 'Could not create the invite — try again?');
      })
      .catch(() => setErr('Could not create the invite — try again?'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);
  const link = inv ? `${window.location.origin}/mira?join=${inv.code}` : '';
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* no-op */ }
  };
  const share = async () => {
    try { await navigator.share({ title: 'Think with me on Mira ✦', text: 'Join my idea session at Xin — we build it together:', url: link }); } catch { /* cancelled */ }
  };
  return (
    <div data-testid="mira-invite-modal" onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 3200, background: 'rgba(20,12,24,0.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#FBF7F2', borderRadius: 20, maxWidth: 360, width: '100%', padding: 22,
        textAlign: 'center', fontFamily: 'system-ui, -apple-system, sans-serif',
        border: '1px solid rgba(232,184,75,0.5)',
      }}>
        <div style={{ fontSize: 26, color: '#E8B84B' }}>✦</div>
        <div style={{ fontWeight: 900, fontSize: 18, color: '#3A1140', marginTop: 4 }}>Invite your table</div>
        <div style={{ fontSize: 12.5, color: '#7A6A80', marginTop: 6, lineHeight: 1.55 }}>
          Up to three friends can join this conversation — each on their own phone.
          They scan, sign in, and you all think with Mira together.
        </div>
        {err && <div data-testid="mira-invite-error" style={{ color: '#B33', fontSize: 12, marginTop: 12 }}>{err}</div>}
        {inv && (
          <>
            <img src={`${BASE}/mira/invite/${inv.code}/qr.png`} alt="Scan to join" data-testid="mira-invite-qr"
              style={{ width: 168, height: 168, marginTop: 14, borderRadius: 12, border: '1px solid #E4D9E7' }} />
            <div data-testid="mira-invite-code" style={{ marginTop: 10, fontWeight: 900, fontSize: 26, letterSpacing: 6, color: '#3A1140' }}>
              {inv.code}
            </div>
            <div style={{ fontSize: 10.5, color: '#8A6B92', marginTop: 2 }}>scan across the table, or send the link</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={copy} data-testid="mira-invite-copy" style={{
                flex: 1, background: '#3A1140', color: '#F5C96A', border: 'none', borderRadius: 999,
                padding: '10px 0', fontWeight: 900, fontSize: 12, cursor: 'pointer',
              }}>{copied ? 'Copied ✓' : 'Copy link'}</button>
              {typeof navigator !== 'undefined' && navigator.share ? (
                <button onClick={share} data-testid="mira-invite-share" style={{
                  flex: 1, background: '#E8B84B', color: '#3A1140', border: 'none', borderRadius: 999,
                  padding: '10px 0', fontWeight: 900, fontSize: 12, cursor: 'pointer',
                }}>Share…</button>
              ) : null}
            </div>
          </>
        )}
        <button onClick={onClose} data-testid="mira-invite-close" style={{
          marginTop: 12, background: 'none', border: 'none', color: '#8A6B92',
          fontSize: 12, cursor: 'pointer', fontWeight: 700,
        }}>Close</button>
      </div>
    </div>
  );
}

export function GroupChip({ names, dark }) {
  if (!names || names.length < 2) return null;
  return (
    <span data-testid="mira-group-chip" style={{
      fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap', borderRadius: 999, padding: '3px 10px',
      color: dark ? '#E8B84B' : '#3A1140',
      background: dark ? 'rgba(232,184,75,0.12)' : 'rgba(58,17,64,0.08)',
      border: '1px solid ' + (dark ? 'rgba(232,184,75,0.35)' : 'rgba(58,17,64,0.2)'),
    }}>
      ✦ {names.join(' · ')}
    </span>
  );
}

// Throttled "I'm typing" ping — call from the textarea onChange in group mode.
export function useTypingPing(conversationId, active) {
  const lastRef = React.useRef(0);
  return React.useCallback(() => {
    if (!active || !conversationId) return;
    const now = Date.now();
    if (now - lastRef.current < 2500) return;
    lastRef.current = now;
    fetch(`${BASE}/mira/typing`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_id: conversationId }),
    }).catch(() => { /* best effort */ });
  }, [conversationId, active]);
}

let dotsInjected = false;
export function TypingDots({ name, dark }) {
  React.useEffect(() => {
    if (dotsInjected || typeof document === 'undefined') return;
    dotsInjected = true;
    const el = document.createElement('style');
    el.textContent = '@keyframes miraTypeDot { 0%,60%,100% { opacity: 0.25; transform: translateY(0); } 30% { opacity: 1; transform: translateY(-2px); } }';
    document.head.appendChild(el);
  }, []);
  if (!name) return null;
  return (
    <div data-testid="mira-typing-dots" style={{
      display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700,
      color: dark ? '#CBB8D0' : '#8A6B92', padding: '2px 4px 8px',
    }}>
      <span>{name} is typing</span>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 5, height: 5, borderRadius: 99, background: '#E8B84B',
          animation: `miraTypeDot 1.1s ease infinite`, animationDelay: `${i * 0.18}s`,
        }} />
      ))}
    </div>
  );
}

// Show the reveal only once per card, and never to the person who saved it.
export function shouldReveal(sr, myFirst) {
  if (!sr || !sr.card_id) return false;
  try {
    const key = 'mira:reveal_seen';
    const seen = JSON.parse(window.localStorage.getItem(key) || '[]');
    if (seen.includes(sr.card_id)) return false;
    window.localStorage.setItem(key, JSON.stringify([...seen.slice(-20), sr.card_id]));
  } catch { return false; }
  if (sr.by && myFirst && sr.by === myFirst) return false;
  return true;
}

let revealInjected = false;
export function SparkReveal({ reveal, onClose }) {
  React.useEffect(() => {
    if (revealInjected || typeof document === 'undefined') return;
    revealInjected = true;
    const el = document.createElement('style');
    el.textContent = `
      @keyframes miraRevealIn { from { opacity: 0; transform: scale(0.82) translateY(24px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      @keyframes miraRevealGlow { 0%,100% { box-shadow: 0 0 40px rgba(232,184,75,0.35); } 50% { box-shadow: 0 0 90px rgba(232,184,75,0.7); } }
      @keyframes miraRevealStar { 0%,100% { opacity: 0.5; transform: scale(1) rotate(0deg); } 50% { opacity: 1; transform: scale(1.25) rotate(18deg); } }
    `;
    document.head.appendChild(el);
  }, []);
  if (!reveal) return null;
  return (
    <div data-testid="mira-spark-reveal" style={{
      position: 'fixed', inset: 0, zIndex: 3600,
      background: 'radial-gradient(120% 120% at 50% 20%, rgba(58,17,64,0.96) 0%, rgba(21,15,26,0.98) 70%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{ animation: 'miraRevealIn 600ms cubic-bezier(0.22,1,0.36,1) both', textAlign: 'center', maxWidth: 380 }}>
        <div style={{ fontSize: 34, color: '#E8B84B', animation: 'miraRevealStar 2.2s ease-in-out infinite' }}>✦</div>
        <div style={{ color: '#E8B84B', fontWeight: 900, fontSize: 13, letterSpacing: 3, textTransform: 'uppercase', marginTop: 8 }}>
          {reveal.by ? `${reveal.by} landed it` : 'The idea landed'}
        </div>
        <div style={{ color: '#F5EFE8', fontWeight: 900, fontSize: 22, marginTop: 6, lineHeight: 1.3 }}>
          &ldquo;{reveal.title}&rdquo;
        </div>
        <img src={`${BASE.replace('/api', '')}${reveal.cover_url}`} alt="Your Spark Card"
          data-testid="mira-spark-reveal-cover"
          style={{
            width: '100%', maxWidth: 300, marginTop: 18, borderRadius: 16,
            border: '1px solid rgba(232,184,75,0.6)', animation: 'miraRevealGlow 2.4s ease-in-out infinite',
          }} />
        <div style={{ color: '#CBB8D0', fontSize: 13, marginTop: 16, lineHeight: 1.6 }}>
          You made this together &mdash; the full card is on its way to everyone&rsquo;s inbox.
        </div>
        <button onClick={onClose} data-testid="mira-spark-reveal-close" style={{
          marginTop: 20, background: '#E8B84B', color: '#3A1140', border: 'none',
          borderRadius: 999, padding: '12px 32px', fontWeight: 900, fontSize: 14,
          cursor: 'pointer', letterSpacing: 0.4, boxShadow: '0 10px 30px rgba(232,184,75,0.3)',
        }}>Beautiful &#10022;</button>
      </div>
    </div>
  );
}
